import { imprimirCupomCozinha } from './EscPosGenerator';

function buscarPorId(id: any, catalogo: any[]): any {
    if (id == null || String(id).trim() === '') return undefined;
    const correspondencias = catalogo.filter(item => item?.id != null && String(item.id) === String(id));
    return correspondencias.length === 1 ? correspondencias[0] : undefined;
}

function impressoraDoCadastro(cadastro: any): string | undefined {
    return typeof cadastro?.impressoraSelecionada === 'string' && cadastro.impressoraSelecionada.trim()
        ? cadastro.impressoraSelecionada : undefined;
}

function numeroPtBr(valor: any): number {
    const numero = typeof valor === 'number' ? valor : Number(String(valor ?? 0).replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(numero) ? numero : 0;
}

function valorPtBr(valor: number): string {
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function prepararComplemento(complemento: any, produto: any, impressora: string): any {
    // Seleção múltipla salva a quantidade no texto e o preço total dessa seleção.
    const descricao = String(complemento.descricao ?? '').trim();
    const prefixo = descricao.match(/^(\d+)x\s+/i);
    const quantidadeSelecionada = prefixo ? Number(prefixo[1]) : 1;
    const quantidadeProduto = numeroPtBr(produto.quantidade);
    if (quantidadeSelecionada <= 0 || quantidadeProduto <= 0) return undefined;

    return {
        id: complemento.id,
        descricao: prefixo ? descricao.slice(prefixo[0].length) : descricao,
        quantidade: quantidadeSelecionada * quantidadeProduto,
        valor: valorPtBr(numeroPtBr(complemento.valor) / quantidadeSelecionada),
        subtotal: valorPtBr(numeroPtBr(complemento.valor) * quantidadeProduto),
        impressoraSelecionada: impressora,
    };
}

function identificarProdutoPrincipal(produto: any, catalogo: any[]): any {
    const principal = Array.isArray(produto.descricao) ? produto.descricao[0] : undefined;
    const id = [produto.id, principal?.id].find(valor => valor != null && String(valor).trim() !== '');

    if (id !== undefined && id !== null && String(id).trim() !== '') {
        return buscarPorId(id, catalogo);
    }

    let nome = String(principal?.descricao ?? produto.descricao ?? produto.nome ?? '').trim();
    if (produto.tipo === 'pizzas') nome = nome.replace(/^1\/\d+\s+/, '');
    const categoria = String(principal?.categoria ?? produto.categoria ?? '').trim();
    if (!nome || !categoria) return undefined;

    const correspondencias = catalogo.filter(item =>
        String(item?.nome ?? '').trim() === nome
        && String(item?.categoria ?? '').trim() === categoria
    );
    return correspondencias.length === 1 ? correspondencias[0] : undefined;
}

function prepararProdutosParciais(produtos: any[], catalogo: any[], modo: 'pedido' | 'mesa'): any[] {
    return produtos.filter((produto: any) => produto && !produto.impresso)
        .reduce((selecionados: any[], produto: any) => {
            const cadastro = identificarProdutoPrincipal(produto, catalogo);
            const impressora = impressoraDoCadastro(cadastro ?? (modo === 'mesa' ? produto : undefined));
            const complementos: any[] = [];
            let valorSeparado = 0;
            const descricao = Array.isArray(produto.descricao) ? produto.descricao.filter((detalhe: any, indice: number) => {
                // Sabores, observações e complementos livres continuam no prato.
                // O índice do grupo identifica os complementos vinculados ao catálogo.
                if (indice === 0 || detalhe?.indiceComplemento == null || detalhe.categoria === 'Observação') return true;
                const cadastroComplemento = buscarPorId(detalhe.id, catalogo);
                const destino = impressoraDoCadastro(cadastroComplemento);
                if (!destino || destino === impressora) return true;

                const separado = prepararComplemento(detalhe, produto, destino);
                if (!separado) return true;
                complementos.push(separado);
                valorSeparado += numeroPtBr(detalhe.valor);
                return false;
            }) : produto.descricao;

            if (impressora || modo === 'mesa') {
                selecionados.push({
                    ...produto, descricao, impressoraSelecionada: impressora,
                    ...(complementos.length > 0 ? {
                        valor: valorPtBr(numeroPtBr(produto.valor) - valorSeparado),
                        subtotal: valorPtBr(numeroPtBr(produto.subtotal) - valorSeparado * numeroPtBr(produto.quantidade)),
                    } : {}),
                });
            }
            selecionados.push(...complementos);
            return selecionados;
        }, []);
}

/** Nas mesas, itens sem destino específico também precisam seguir para produção. */
export function agruparParciaisMesa(projeto: any, dadosEmpresa: any): Array<{ impressora: string; produtos: any[] }> {
    if (!(dadosEmpresa?.multiImpressao > 1) || !Array.isArray(projeto?.produtos)) return [];
    const catalogo = Array.isArray(dadosEmpresa.catalogoProdutos) ? dadosEmpresa.catalogoProdutos : [];
    const grupos = new Map<string, any[]>();
    prepararProdutosParciais(projeto.produtos, catalogo, 'mesa').forEach(produto => {
        const impressora = produto.impressoraSelecionada || '';
        const grupo = grupos.get(impressora) || [];
        grupo.push(produto);
        grupos.set(impressora, grupo);
    });
    return Array.from(grupos, ([impressora, produtos]) => ({ impressora, produtos }));
}

/**
 * Acrescenta os cupons de produção à impressão inicial do pedido completo.
 * A configuração do catálogo da empresa prevalece sobre os dados do pedido.
 * Não deve ser chamada nas ações de reimpressão.
 */
export function imprimirParciaisPedido(projeto: any, dadosEmpresa: any): boolean {
    if (!projeto || !(dadosEmpresa?.multiImpressao > 1)) return false;

    if (projeto.pedidoIfood || projeto.pedido99Food || projeto.ehComanda) return false;

    if (!Array.isArray(projeto.produtos) || !Array.isArray(dadosEmpresa.catalogoProdutos)) {
        return false;
    }

    try {
        const produtos = prepararProdutosParciais(projeto.produtos, dadosEmpresa.catalogoProdutos, 'pedido');

        if (produtos.length === 0) return false;

        return imprimirCupomCozinha({
            ...projeto,
            clienteNome: projeto.clienteNome || projeto.cliente?.nome || projeto.nomeCliente || '',
            produtos,
        }, dadosEmpresa);
    } catch (error) {
        // O cupom completo já foi enfileirado; uma falha no parcial não deve
        // interromper a conclusão do cadastro nem provocar seu reenvio.
        console.error('Erro ao preparar cupons parciais do pedido:', error);
        return false;
    }
}
